import { Card, Typography } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import arrayMutators from 'final-form-arrays'
import setFieldTouched from 'final-form-set-field-touched'
import { useEffect, useState } from 'react'
import {
  ArrayInput,
  email,
  maxLength,
  minLength,
  NumberInput,
  required,
  SelectInput,
  SimpleFormIterator,
  TextInput,
  useNotify,
  useRedirect,
} from 'react-admin'
import { Form } from 'react-final-form'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { ConfirmDialog } from 'shared/components/Dialog/ConfirmDialog'
import { FileUploadWithSlideoutPreview } from 'shared/components/FileUpload/FileUploadWithSlideoutPreview'
import { Text } from 'shared/components/Text/Text'
import { HandlerOf } from 'shared/helpers/typeHelper'
import { breakpoints } from 'shared/theme/breakpoints'
import styled from 'styled-components'

import { createExternalLoad, parseRateConfirmation, upload } from 'api/jobs'
import { BrokerStatus } from 'components/Brokers/BrokerStatusTransitions'
import { BrokerReferenceInput, BrokerReferenceInputClassName } from 'components/Common/Brokers'
import { AssignedDriverReferenceInput, StopDateTimeInputWindow } from 'components/Fields'
import {
  DocumentType,
  LoadingTypeChoices,
  StopTypeDetailChoices,
  SUPPORTED_EQUIPMENT_CHOICES,
} from 'helpers/constants'
import { getCountryFromCode } from 'helpers/countries'
import { cleanJobFormValues } from 'helpers/jobs'
import { getReadableErrorMessage } from 'helpers/utils'
import { validateInteger } from 'helpers/validators'
import { RateConParsedFields } from 'models/jobs'
import { getStateChoices } from '../../helpers/states'

interface RateConParsedResponse {
  id: string
  rate_con_fields: RateConParsedData
}

interface RateConParsedData {
  parsed_fields: RateConParsedFields
}

export const ExternalBook = () => {
  const [gptResponse, setGptResponse] = useState<RateConParsedResponse | null>(null)
  const [rateConfirmation, setRateConfirmation] = useState<File | undefined>(undefined)
  const [isParsingRateConfirmation, setParsingRateConfirmation] = useState(false)

  return (
    <Card style={{ padding: 20 }}>
      <GridWrap>
        <ExternalBookForm
          gptResponse={gptResponse}
          rateConfirmation={rateConfirmation}
          isParsingRateConfirmation={isParsingRateConfirmation}
        />
        <ParseRateConfirmation
          rateConfirmation={rateConfirmation}
          setRateConfirmation={setRateConfirmation}
          setGptResponse={setGptResponse}
          gptResponse={gptResponse}
          isParsingRateConfirmation={isParsingRateConfirmation}
          setParsingRateConfirmation={setParsingRateConfirmation}
        />
      </GridWrap>
    </Card>
  )
}

interface ParseRateConfirmationProps {
  rateConfirmation: File | undefined
  setRateConfirmation: HandlerOf<File | undefined>
  setGptResponse: HandlerOf<RateConParsedResponse | null>
  gptResponse: RateConParsedResponse | null
  isParsingRateConfirmation: boolean
  setParsingRateConfirmation: HandlerOf<boolean>
}

function ParseRateConfirmation({
  rateConfirmation,
  setRateConfirmation,
  setGptResponse,
  gptResponse,
  isParsingRateConfirmation,
  setParsingRateConfirmation,
}: ParseRateConfirmationProps) {
  const notify = useNotify()
  const uploadAndParseRateConfirmation = () => {
    setParsingRateConfirmation(true)
    parseRateConfirmation(rateConfirmation)
      .then((response: any) => {
        setGptResponse(response.data)
        setParsingRateConfirmation(false)
      })
      .catch((error: any) => {
        setParsingRateConfirmation(false)
        notify(error.message, { type: 'error' })
      })
  }
  return (
    <FlexBox alignItems="center" gap="15px" flexDirection="column">
      <Typography variant="h5">Upload a rate confirmation to get started</Typography>
      <FileUploadWithSlideoutPreview
        file={rateConfirmation}
        setFile={setRateConfirmation}
        positionExpandedUnderToolbar
      />
      {!gptResponse && (
        <Button
          variant="contained"
          color="primary"
          onClick={uploadAndParseRateConfirmation}
          disabled={isParsingRateConfirmation}
        >
          Parse Rate Confirmation
        </Button>
      )}
    </FlexBox>
  )
}
interface ExternalBookFormProps {
  gptResponse: RateConParsedResponse | null
  rateConfirmation: File | undefined
  isParsingRateConfirmation: boolean
}

function ExternalBookForm({
  gptResponse,
  rateConfirmation,
  isParsingRateConfirmation,
}: ExternalBookFormProps) {
  const redirect = useRedirect()
  const notify = useNotify()

  async function handleCreateJob(values: any) {
    if (!rateConfirmation) {
      notify('Please upload a rate confirmation', { type: 'error' })
      return
    }

    values = cleanJobFormValues(values)
    values.rate_con_parsed_data_id = gptResponse?.id
    let newJob = await createExternalLoad(values)
    let formData = new FormData()
    formData.append('attachments', rateConfirmation, rateConfirmation?.name)
    formData.append('type', DocumentType.RateConfirmation)
    formData.append('rate_con_parsed_data_id', gptResponse?.id || '')
    let newRateCon = await upload(newJob.data.id, formData)

    // Display warning if the job failed to auto approve
    let autoApproveWarnings = newJob?.data?.warnings
    if (autoApproveWarnings) {
      if (
        autoApproveWarnings?.load?.stops[0]?.['date_start'] ||
        autoApproveWarnings?.load?.stops[0]?.['date_end']
      ) {
        notify("Job couldn't be approved as appointment times are too far in the past.", { type: 'warning' })
      } else {
        notify(JSON.stringify(autoApproveWarnings), { type: 'warning' })
      }
    }

    redirect(`/jobs/${newRateCon.job}/show`)
  }

  if (!gptResponse && !isParsingRateConfirmation) return <FlexBox />
  if (isParsingRateConfirmation)
    return (
      <FlexBox alignItems="center" justifyContent="center" isColumn>
        <Typography variant="h6">Parsing rate confirmation...</Typography>
        <CircularProgress />
      </FlexBox>
    )
  return (
    <Form
      initialValues={gptResponse?.rate_con_fields.parsed_fields}
      onSubmit={handleCreateJob}
      // @ts-ignore
      mutators={{
        ...arrayMutators,
        setTouched: setFieldTouched,
      }}
      render={(formProps) => (
        <InnerExternalBookForm formProps={formProps} handleCreateJob={handleCreateJob} />
      )}
    />
  )
}

const InnerExternalBookForm = ({ formProps, handleCreateJob }: any) => {
  const [stateChoices, setStateChoices] = useState<any>([])
  const [validationTriggered, setValidationTriggered] = useState(false)
  const notify = useNotify()
  useEffect(() => {
    if (!validationTriggered) {
      formProps.form.getRegisteredFields().forEach((field: string) => {
        formProps.form.mutators.setTouched(field, true)
      })
      setValidationTriggered(true)
    }
  }, [formProps, validationTriggered])

  useEffect(() => {
    const country = getCountryFromCode('US')
    setStateChoices(getStateChoices(country?.states(true)))
  }, [])

  async function handleCreateJobError(error: any) {
    notify(getReadableErrorMessage(error), { type: 'error' })
  }
  return (
    <form onSubmit={formProps.handleSubmit}>
      <Typography variant="h6">Confirm load information</Typography>
      <LoadDetailsGridWrap>
        <TextInput source="source_human_id" label="Broker load ID" validate={required()} />
        <BrokerReferenceInput
          source="broker"
          label="Broker"
          reference="brokers"
          filter={{ status__not: BrokerStatus.Invalid.id }}
          filterToQuery={(searchText: any) => ({ search: searchText })}
          containerClassName={BrokerReferenceInputClassName.load}
          validate={required()}
        />
        <TextInput source="contact_name" label="Contact name" />
        <TextInput source="contact_phone" label="Contact phone" />
        <TextInput source="contact_email" label="Contact email" validate={email()} />
        <NumberInput source="trip_rate" label="Trip rate" validate={required()} />
        <AssignedDriverReferenceInput />
        <SelectInput source="equipment" choices={SUPPORTED_EQUIPMENT_CHOICES} validate={required()} />
        <NumberInput source="truck_length_ft" validate={validateInteger} />
        <NumberInput source="truck_weight_lb" validate={validateInteger} />
      </LoadDetailsGridWrap>
      <Typography variant="h6">Review stops</Typography>

      <ArrayInput source="stops">
        <SimpleFormIterator>
          <SelectInput source="type_detail" label="Type detail" choices={StopTypeDetailChoices} />
          <SelectInput source="loading_type" label="Loading type" choices={LoadingTypeChoices} />
          {
            // @ts-ignore
            <StopDateTimeInputWindow />
          }
          <TextInput source="location_name" label="Facility name" />
          <TextInput source="location_address1" label="Location address 1" />
          <TextInput source="location_address2" label="Location address 2" />
          <TextInput source="location_city" label="City" validate={required()} />
          <SelectInput
            source="location_state"
            label="State"
            choices={stateChoices}
            validate={[required(), maxLength(2), minLength(2)]}
          />
          <TextInput source="location_zip" label="Zip" />
          <TextInput source="notes" label="Notes" />
        </SimpleFormIterator>
      </ArrayInput>
      <ConfirmDialog
        handleConfirm={() => handleCreateJob(formProps.values)}
        handleError={handleCreateJobError}
        dialogContent={
          <Text variant="header2" align="center">
            Are you sure you want to create this job?
          </Text>
        }
        confirmLabel="Confirm Job Creation"
      >
        <Button variant="contained" color="primary" disabled={!formProps.valid}>
          Create Job
        </Button>
      </ConfirmDialog>
    </form>
  )
}

const GridWrap = styled.div`
  display: grid;
  grid-template-columns: 424px 1fr;
  gap: 80px;

  @media ${breakpoints.sm} {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`
const LoadDetailsGridWrap = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
`
